import { z } from 'zod'

/**
 * Test mode, at the ORGANIZATION level (PLAN v3, M5): the organizer runs a real community (real DAVINCI processes for
 * its votings on OP Mainnet, real votes) but identity verification goes through the Didit SANDBOX application: no real ID
 * is checked, the email / phone codes are accepted whatever their value, and the KYC outcome is a sandbox scenario.
 * Chosen when the organization is created, fixed once it is published. Every voting of a test organization is
 * marked "[TEST]" on chain, and its emails never reach real members (M4: recipients rewritten to the test sink).
 *
 * Outcome of a member's sandbox verification:
 * - `random`: the server picks a scenario from the sandbox catalog with the organizer's mix (approved / in review /
 *   declined) and arms it at session creation (`sandbox_scenario`). The hosted Didit screen shows the chosen
 *   scenario (the voter can still change it, it is a test).
 * - `voter_choice`: no scenario; Didit's hosted sandbox screen asks the voter "Choose a test result".
 */
export const TEST_OUTCOMES = ['random', 'voter_choice'] as const
export const TestOutcomeSchema = z.enum(TEST_OUTCOMES)
export type TestOutcome = z.infer<typeof TestOutcomeSchema>

export const DEFAULT_TEST_MIX = { approved: 70, inReview: 20, declined: 10 } as const

const share = z.number().int().min(0).max(100)
/** Percentages of each outcome for `random` (must add up to 100). */
export const TestMixSchema = z
  .object({
    approved: share.default(DEFAULT_TEST_MIX.approved),
    inReview: share.default(DEFAULT_TEST_MIX.inReview),
    declined: share.default(DEFAULT_TEST_MIX.declined),
  })
  .refine((m) => m.approved + m.inReview + m.declined === 100, {
    message: 'The three shares must add up to 100',
    path: ['approved'],
  })
export type TestMix = z.infer<typeof TestMixSchema>

export type TestBucket = keyof TestMix

/** Didit status a sandbox scenario ends with in the hosted flow (catalog `default_status`). */
export type SandboxStatus = 'Approved' | 'In Review' | 'Declined'

export interface TestScenario {
  slug: string
  bucket: TestBucket
  /** Catalog `default_status`. `simulate` must be called with it as `new_status` (review_* default to Declined there). */
  status: SandboxStatus
  label: string
}

/**
 * Realistic scenarios of Didit's sandbox catalog (`GET /v1/sandbox/scenarios/`, checked 2026-10-05) that apply to
 * our workflow (OCR + LIVENESS + FACE_MATCH, optional IP_ANALYSIS). Review scenarios that need features we do not
 * use (AML, proof of address, database validation) or a workflow setting we do not always have (minimum age →
 * review) are left out.
 */
export const TEST_SCENARIOS: readonly TestScenario[] = [
  { slug: 'approve', bucket: 'approved', status: 'Approved', label: 'Happy path, all checks approve' },
  {
    slug: 'review_face_match_borderline',
    bucket: 'inReview',
    status: 'In Review',
    label: 'Face match: borderline similarity',
  },
  {
    slug: 'decline_face_match_low_similarity',
    bucket: 'declined',
    status: 'Declined',
    label: 'Face match: similarity below the decline threshold',
  },
  { slug: 'decline_document_expired', bucket: 'declined', status: 'Declined', label: 'ID document expired' },
  { slug: 'decline_liveness_attack', bucket: 'declined', status: 'Declined', label: 'Liveness: face attack detected' },
]

/**
 * Our own duplicate-face fixture (PLAN v4 R19): the Didit sandbox runs no face search, so the session is armed with
 * `approve` at Didit and the API adds a SYNTHETIC same-organization duplicate face at decision time ⇒ review
 * `duplicate_face` with a synthetic matched member.
 */
export const DUPLICATE_FACE_TEST_SCENARIO = 'review_duplicate_face'

/** Scenarios of workflows with a selfie but no document (unique human only, PLAN v4). */
export const LIVENESS_TEST_SCENARIOS: readonly TestScenario[] = [
  { slug: 'approve', bucket: 'approved', status: 'Approved', label: 'Happy path, all checks approve' },
  {
    slug: DUPLICATE_FACE_TEST_SCENARIO,
    bucket: 'inReview',
    status: 'Approved',
    label: 'Unique human: the face matches another member (synthetic)',
  },
  { slug: 'decline_liveness_attack', bucket: 'declined', status: 'Declined', label: 'Liveness: face attack detected' },
]

/** Extra scenarios when the workflow has a proof-of-address step (PLAN v4). */
export const POA_TEST_SCENARIOS: readonly TestScenario[] = [
  {
    slug: 'review_poa_partial_match',
    bucket: 'inReview',
    status: 'In Review',
    label: 'Proof of address: partial name / address match',
  },
  {
    slug: 'decline_poa_address_mismatch',
    bucket: 'declined',
    status: 'Declined',
    label: 'Proof of address: address mismatch',
  },
]

const ALL_TEST_SCENARIOS: readonly TestScenario[] = [
  ...TEST_SCENARIOS,
  ...LIVENESS_TEST_SCENARIOS,
  ...POA_TEST_SCENARIOS,
].filter((s, i, all) => all.findIndex((x) => x.slug === s.slug) === i)

export const testScenarioBySlug = (slug: string | null | undefined): TestScenario | null =>
  (slug && ALL_TEST_SCENARIOS.find((s) => s.slug === slug)) || null

/**
 * The sandbox catalog for a workflow (PLAN v4 §8): document workflows keep TEST_SCENARIOS, selfie-only workflows use LIVENESS_TEST_SCENARIOS, a proof-of-address step adds POA_TEST_SCENARIOS;
 * a proof-of-address-only workflow uses `approve` + the PoA scenarios.
 */
export function testScenariosFor(f: { document: boolean; liveness: boolean; poa: boolean }): TestScenario[] {
  const base: TestScenario[] = f.document
    ? [...TEST_SCENARIOS]
    : f.liveness
      ? [...LIVENESS_TEST_SCENARIOS]
      : [TEST_SCENARIOS[0]!]
  return f.poa ? [...base, ...POA_TEST_SCENARIOS] : base
}

/**
 * Pick a scenario for one KYC session: bucket by the mix, then uniformly among the bucket's scenarios of `catalog`
 * (default: the document catalog). A bucket without scenarios in the catalog falls back to `approve`.
 * `random` returns a float in [0, 1) (Math.random-like; tests inject a seeded generator).
 */
export function pickTestScenario(
  mix: TestMix,
  random: () => number,
  catalog: readonly TestScenario[] = TEST_SCENARIOS
): TestScenario {
  const buckets: TestBucket[] = ['approved', 'inReview', 'declined']
  const total = buckets.reduce((n, b) => n + mix[b], 0)
  let x = random() * (total > 0 ? total : 100)
  let bucket: TestBucket = 'approved'
  for (const b of buckets) {
    if (mix[b] <= 0) continue
    bucket = b
    if (x < mix[b]) break
    x -= mix[b]
  }
  const options = catalog.filter((s) => s.bucket === bucket)
  if (!options.length) return TEST_SCENARIOS[0]!
  return options[Math.min(options.length - 1, Math.floor(random() * options.length))]!
}

/** Small deterministic PRNG (mulberry32) for seeded scenario selection in tests. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** The code shown to members of a test organization (the Didit sandbox accepts any well-formed code). */
export const TEST_MODE_CODE = '123456'

/** Marker added to the on-chain (DAVINCI metadata) title of the processes of a test organization's votings. */
export const TEST_TITLE_MARKER = '[TEST]'
export const TEST_DESCRIPTION_NOTICE = 'Test election: identities were not verified (Didit sandbox).'

/** Title as published on chain. */
export const onChainTitle = (title: string, testMode: boolean) =>
  testMode && !title.startsWith(TEST_TITLE_MARKER) ? `${TEST_TITLE_MARKER} ${title}` : title

/** Description as published on chain. */
export const onChainDescription = (description: string, testMode: boolean) =>
  testMode ? (description ? `${TEST_DESCRIPTION_NOTICE}\n\n${description}` : TEST_DESCRIPTION_NOTICE) : description
